import {
    addDateEventListener,
    debouncedFetchDataAndUpdateFields,
    formatDate,
    formatNumber,
    formatToCurrency,
    handleAmountInput,
    initLoanBtnListener,
    initSteps,
    mapSteps,
    updateRangeBarColor
} from "./calculatorLogicPreview";

const prevCalcInit = (calcType, siteUrl, apiDomain, context) => {
    const {
        inputInstallments,
        rangeInstallments,
        fromInstallmentsDisplay,
        toInstallmentsDisplay,
        inputAmountLeft,
        rangeAmountLeft,
        fromAmountLeftDisplay,
        toAmountLeftDisplay,
        fromPreviewPeriodDisplay,
        toPreviewPeriodDisplay,
        inputCash,
        rangeCash,
        fromCashDisplay,
        toCashDisplay,
        inputPeriod,
        rangePeriod,
        installmentAmount,
        rrso,
        loanButton,
        datepicker,
        formLink,
    } = context.calculatorFields;

    let config = context.config;

    let stepsPeriod = [];
    //Min & max values for fields
    let installmentsMin;
    let installmentsMax;
    let amountLeftMin;
    let amountLeftMax;
    let cashMin;
    let cashMax;
    let periodMin;
    let periodMax;

    let currentState = {
        currentInstallmentValue: 0,
        currentAmountLeftValue: 0,
        currentCashValue: 0,
        currentPeriodValue: 0,
        currentDate: new Date(),
        currentOffer: {},
        currentBtnLoanLink: ''
    }

    let previousInputInstallmentsValue = '';
    let previousInputAmountLeftValue = '';
    let previousInputCashValue = '';
    let previousInputPeriodValue = '';

    const rangeBarColors = {
        leftSide: 'black',
        rightSide: '#FFF'
    }

    //config = configuration;
    setupFields();

    //Listeners for range inputs
    addRangeEventListeners(inputInstallments, rangeInstallments);
    addRangeEventListeners(inputAmountLeft, rangeAmountLeft);
    addRangeEventListeners(inputCash, rangeCash);
    addRangeEventListeners(inputPeriod, rangePeriod);

    addInputEventListeners(inputInstallments);
    addInputEventListeners(inputAmountLeft);
    addInputEventListeners(inputCash);
    addInputEventListeners(inputPeriod);

    addDateEventListener(calcType, datepicker, context, currentState, siteUrl, formLink);

    function setupFields() {
        setupSliders();
        setupInfoFields();
        debouncedFetchDataAndUpdateFields(apiDomain, currentState.currentAmountLeftValue + currentState.currentCashValue, currentState.currentPeriodValue, currentState.currentDate, context, null, currentState)
            .then(buttonLinks => {
                currentState.currentBtnLoanLink = siteUrl + buttonLinks.currentBtnLoanLink;
                formLink.href = apiDomain + buttonLinks.formLink;
                initLoanBtnListener(loanButton, currentState);
            })
            .catch(error => {
                console.error('Error fetching data and updating fields:', error);
            });
        //Find current offer
        setPeriodFields(currentState.currentAmountLeftValue + currentState.currentCashValue);
    }

    function setupSliders() {
        /**
         * Installment values
         */
        inputInstallments.value = formatToCurrency(config.customerInstallment); //Text input value
        previousInputInstallmentsValue = config.customerInstallment; //Save the previous value for restoring when focus is lost
        rangeInstallments.value = config.customerInstallment; //Range input value
        fromInstallmentsDisplay.textContent = formatNumber(parseInt(rangeInstallments.min)) + ' zł'; //From (range)
        // value
        toInstallmentsDisplay.textContent = formatNumber(parseInt(rangeInstallments.max)) + ' zł'; //To (range) value
        installmentsMin = parseInt(rangeInstallments.min); //Min. for calculations
        installmentsMax = parseInt(rangeInstallments.max); //Max for calculations
        currentState.currentInstallmentValue = config.customerInstallment; //Current state
        updateRangeBarColor(config.customerInstallment, rangeInstallments, installmentsMin, installmentsMax, rangeBarColors);

        /**
         * Amount Left fields
         */
        const defaultAmountLeft = 10000;
        inputAmountLeft.value = formatToCurrency(defaultAmountLeft);
        previousInputAmountLeftValue = defaultAmountLeft;
        rangeAmountLeft.max = config.toAmount;
        rangeAmountLeft.value = defaultAmountLeft;

        amountLeftMin = parseInt(rangeAmountLeft.min);
        amountLeftMax = config.toAmount;
        fromAmountLeftDisplay.textContent = formatNumber(parseInt(rangeAmountLeft.min)) + ' zł';
        toAmountLeftDisplay.textContent = formatNumber(config.toAmount) + ' zł';
        currentState.currentAmountLeftValue = defaultAmountLeft;
        updateRangeBarColor(defaultAmountLeft, rangeAmountLeft, amountLeftMin, amountLeftMax, rangeBarColors);

        /**
         * Cash fields
         */
        const defaultCash = 10000;
        inputCash.value = formatToCurrency(defaultCash);
        previousInputCashValue = defaultCash;
        rangeCash.max = config.toAmount;
        rangeCash.value = defaultCash;
        cashMin = parseInt(rangeCash.min);
        cashMax = config.toAmount;
        fromCashDisplay.textContent = formatNumber(rangeCash.min) + ' zł';
        toCashDisplay.textContent = formatNumber(config.toAmount) + ' zł';
        currentState.currentCashValue = defaultCash;
        updateRangeBarColor(defaultCash, rangeCash, cashMin, cashMax, rangeBarColors);

        /**
         * Period values
         */
        inputPeriod.value = config.defaultPeriod + ' mies.';
        previousInputPeriodValue = config.defaultPeriod;
        periodMin = config.fromPeriod;
        periodMax = config.toPeriod;
        rangePeriod.min = config.fromPeriod;
        rangePeriod.max = config.toPeriod;
        rangePeriod.value = config.defaultPeriod;
        stepsPeriod = config.periodSteps;
        stepsPeriod = mapSteps(stepsPeriod, inputPeriod, 'preview');
        initSteps(stepsPeriod, rangePeriod, config.defaultPeriod);
        currentState.currentPeriodValue = config.defaultPeriod;
        fromPreviewPeriodDisplay.textContent = `${config.fromPeriod} ${getMonthDeclension(config.fromPeriod)}`;
        toPreviewPeriodDisplay.textContent = `${config.toPeriod} ${getMonthDeclension(config.toPeriod)}`;
        //Progress bar
        updateRangeBarColor(config.defaultPeriod, rangePeriod, periodMin, periodMax, rangeBarColors);
    }

    function setupInfoFields() {
        //Installment
        const installmentDefault = 0;
        if (installmentAmount) installmentAmount.textContent = installmentDefault.toLocaleString('pl-PL', {
            style: 'currency',
            currency: 'PLN'
        });
        //RRSO
        if (rrso) rrso.textContent = config.rrso.toFixed(2).replace('.', ',') + "%";

        //Date
        datepicker.min = formatDate(config.minFirstRepaymentDate);
        datepicker.max = formatDate(config.maxFirstRepaymentDate);
        currentState.currentDate = formatDate(config.defaultFirstRepaymentDate);
    }

    function addRangeEventListeners(input, range) {
        range.addEventListener('input', () => {
            const rangeValue = parseInt(range.value);
            let min = null;
            let max = null;

            switch (range) {
                case rangeInstallments:
                    min = installmentsMin;
                    max = installmentsMax;
                    input.value = formatToCurrency(rangeValue);
                    currentState.currentInstallmentValue = rangeValue;
                    break;
                case rangeAmountLeft:
                    min = amountLeftMin;
                    max = amountLeftMax;
                    input.value = formatToCurrency(rangeValue);
                    currentState.currentAmountLeftValue = rangeValue;
                    break;
                case rangeCash:
                    min = cashMin;
                    max = cashMax;
                    input.value = formatToCurrency(rangeValue);
                    currentState.currentCashValue = rangeValue;
                    break;
                case rangePeriod:
                    min = periodMin;
                    max = periodMax;
                    input.value = rangeValue + ' mies.';
                    currentState.currentPeriodValue = rangeValue;
                    initSteps(stepsPeriod, rangePeriod, rangeValue);
                    break;
            }
            //Progress bar
            updateRangeBarColor(rangeValue, range, min, max, rangeBarColors);
            checkMaxCash();
            setPeriodFields(currentState.currentAmountLeftValue + currentState.currentCashValue);

            debouncedFetchDataAndUpdateFields(apiDomain, currentState.currentAmountLeftValue + currentState.currentCashValue, currentState.currentPeriodValue, currentState.currentDate, context, null, currentState)
                .then(buttonLinks => {
                    currentState.currentBtnLoanLink = siteUrl + buttonLinks.currentBtnLoanLink;
                    formLink.href = apiDomain + buttonLinks.formLink;
                })
                .catch(error => {
                    console.error('Fetch error:', error);
                });
        });
    }

    /**
     * Check if sum of inputAmount left and inputCash is not bigger than max and correct inputCash value
     */
    function checkMaxCash() {
        const sum = currentState.currentAmountLeftValue + currentState.currentCashValue;
        if (sum > amountLeftMax) {
            const excessAmount = sum - amountLeftMax;
            currentState.currentCashValue = currentState.currentCashValue - excessAmount;
            inputCash.value = formatToCurrency(currentState.currentCashValue);
            rangeCash.value = currentState.currentCashValue;
            updateRangeBarColor(currentState.currentCashValue, rangeCash, cashMin, cashMax, rangeBarColors);
        }
    }

    function addInputEventListeners(input) {
        //Allow only numeric characters
        input.addEventListener('input', function (event) {
            event.target.value = event.target.value.replace(/[^0-9]/g, '');
        });

        input.addEventListener('focus', function () {
            input.value = ''; // Clear the input on focus
        });

        input.addEventListener('blur', function () {
            let inputValue = parseInt(input.value);

            inputValue = checkAndRestorePreviousValue(input, inputValue);

            let handlePromise;
            switch (input) {
                case inputInstallments:
                    handlePromise = handleAmountInput(input, inputValue, rangeInstallments, currentState, previousInputInstallmentsValue, installmentsMin, installmentsMax, rangeBarColors, context)
                        .then(({ previousValue }) => {
                            previousInputInstallmentsValue = previousValue;
                            updateRangeBarColor(currentState.currentInstallmentValue, rangeInstallments, installmentsMin, installmentsMax, rangeBarColors);
                        });
                    break;
                case inputAmountLeft:
                    handlePromise = handleAmountInput(input, inputValue, rangeAmountLeft, currentState, previousInputAmountLeftValue, amountLeftMin, amountLeftMax, rangeBarColors, context)
                        .then(({ previousValue }) => {
                            previousInputAmountLeftValue = previousValue;
                            updateRangeBarColor(currentState.currentAmountLeftValue, rangeAmountLeft, amountLeftMin, amountLeftMax, rangeBarColors);
                        });
                    break;
                case inputCash:
                    handlePromise = handleAmountInput(input, inputValue, rangeCash, currentState, previousInputCashValue, cashMin, cashMax, rangeBarColors, context)
                        .then(({ previousValue }) => {
                            previousInputCashValue = previousValue;
                            updateRangeBarColor(currentState.currentCashValue, rangeCash, cashMin, cashMax, rangeBarColors);
                        });
                    break;
                case inputPeriod:
                    handlePromise = handleAmountInput(input, inputValue, rangePeriod, currentState, previousInputPeriodValue, periodMin, periodMax, rangeBarColors, context)
                        .then(({ previousValue }) => {
                            previousInputPeriodValue = previousValue;
                            updateRangeBarColor(currentState.currentPeriodValue, rangePeriod, periodMin, periodMax, rangeBarColors);
                        });
                    break;
            }

            checkMaxCash();
            setPeriodFields(currentState.currentAmountLeftValue + currentState.currentCashValue);

            handlePromise
                .then(() => {
                    const amount = currentState.currentAmountLeftValue + currentState.currentCashValue;
                    return debouncedFetchDataAndUpdateFields(apiDomain, amount, currentState.currentPeriodValue, currentState.currentDate, context, null, currentState);
                })
                .then(buttonLinks => {
                    currentState.currentBtnLoanLink = siteUrl + buttonLinks.currentBtnLoanLink;
                    formLink.href = apiDomain + buttonLinks.formLink;
                })
                .catch(error => {
                    console.error('Fetch error:', error);
                });
        });

        //Add enter listeners
        input.addEventListener('keyup', function (event) {
            if (event.key === 'Enter') {
                input.blur();
            }
        })
    }

    function checkAndRestorePreviousValue(input, inputValue) {
        if (input.value === '') {
            if (input === inputInstallments) {
                input.value = formatToCurrency(currentState.currentInstallmentValue);
                inputValue = currentState.currentInstallmentValue;
            } else if (input === inputAmountLeft) {
                input.value = formatToCurrency(currentState.currentAmountLeftValue);
                inputValue = currentState.currentAmountLeftValue;
            } else if (input === inputCash) {
                input.value = formatToCurrency(currentState.currentCashValue);
                inputValue = currentState.currentCashValue;
            } else if (input === inputPeriod) {
                input.value = currentState.currentPeriodValue + ' mies.';
                inputValue = currentState.currentPeriodValue;
            }
        }
        return inputValue;
    }

    function setPeriodFields(sum) {
        updateOffer(sum);
        if (Object.keys(currentState.currentOffer).length > 0) { //Check if there is current offer
            //Set other input and range values
            if (!(currentState.currentPeriodValue >= currentState.currentOffer.fromPeriod &&
                currentState.currentPeriodValue <= currentState.currentOffer.toPeriod)) {
                inputPeriod.value = currentState.currentOffer.defaultPeriod + ' mies.';
                rangePeriod.min = currentState.currentOffer.fromPeriod;
                rangePeriod.max = currentState.currentOffer.toPeriod;
                fromPeriodDisplay.textContent = currentState.currentOffer.fromPeriod;
                toPeriodDisplay.textContent = currentState.currentOffer.toPeriod;
                periodMin = currentState.currentOffer.fromPeriod;
                periodMax = currentState.currentOffer.toPeriod;
                rangePeriod.value = currentState.currentOffer.defaultPeriod;
                currentState.currentPeriodValue = currentState.currentOffer.defaultPeriod;
            }
            initSteps(stepsPeriod, rangePeriod, currentState.currentPeriodValue);
            //Progress bar
            updateRangeBarColor(currentState.currentPeriodValue, rangePeriod, periodMin, periodMax, rangeBarColors);
        }
    }

    function updateOffer(currentSum) {
        //Search for exact offer
        const exactOffer = config.offers.find(offer =>
            currentSum >= offer.fromAmount &&
            currentSum <= offer.toAmount &&
            currentState.currentPeriodValue >= offer.fromPeriod &&
            currentState.currentPeriodValue <= offer.toPeriod
        );
        if (exactOffer) {
            currentState.currentOffer = exactOffer;
        } else {
            //Check which input was changed
            //There is no exact offer for that input and "Amount" field was changed, so search for closest offer with that amount
            let amountOffer = config.offers.find(offer =>
                currentSum >= offer.fromAmount && currentSum <= offer.toAmount
            )

            if (amountOffer) {
                currentState.currentOffer = amountOffer;
            } else {
                //If there is no offer with that amount, find the closest offer by this amount
                amountOffer = findClosestOffer(currentSum);
                if (amountOffer) {
                    currentState.currentOffer = amountOffer;
                }
            }
        }
    }

    // Function to find the closest offer by period
    function findClosestOffer(currentValue) {
        return config.offers.reduce((closest, offer) => {
            let currentDifference, newDifference;
            currentDifference = Math.abs(currentValue - closest.fromAmount);
            newDifference = Math.abs(currentValue - offer.fromAmount);

            return newDifference < currentDifference ? offer : closest;
        }, config.offers[0]); // Start with the first offer as the initial closest
    }

    function getMonthDeclension(number) {
        if (number === 1) {
            return 'miesiąc';
        } else if (number % 10 >= 2 && number % 10 <= 4 && (number % 100 < 10 || number % 100 >= 20)) {
            return 'miesiące';
        } else {
            return 'miesięcy';
        }
    }
}

export { prevCalcInit };
